import React from 'react';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import io from 'socket.io-client';
import { Provider as ReduxProvider } from 'react-redux';
import { ApolloProvider } from 'react-apollo';
import StyleContext from 'isomorphic-style-loader/StyleContext';
import ApplicationContext from './ApplicationContext';
import SocketContext from './SocketContext';
import moment from 'moment';
import { isRTL } from '../helpers/formatLocale';
import { initializeApp } from "firebase/app";
import { getMessaging, onMessage } from "firebase/messaging";
import { socketUrl, firebaseConfig, enableFirebase } from '../config';

const secure = socketUrl && socketUrl.indexOf('https://') >= 0 ? true : false;

let app;
if (enableFirebase) app = initializeApp(firebaseConfig);

export const socket = io(socketUrl,
  {
    secure,
    transports: ['websocket'],
    reconnection: true,
    forceNew: true,
    rejectUnauthorized: false,
    autoConnect: true
  }
);

const ContextType = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  // insertCss: PropTypes.func.isRequired,
  // Universal HTTP client
  fetch: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  query: PropTypes.object,
  // Integrate Redux
  // http://redux.js.org/docs/basics/UsageWithReact.html
  ...ReduxProvider.childContextTypes,
  // Apollo Client
  client: PropTypes.object.isRequired,
  // ReactIntl
  intl: IntlProvider.childContextTypes.intl,
  locale: PropTypes.string,
};

/**
 * The top-level React component setting context (global) variables
 * that can be accessed from all the child components.
 *
 * https://facebook.github.io/react/docs/context.html
 *
 * Usage example:
 *
 *   const context = {
 *     history: createBrowserHistory(),
 *     store: createStore(),
 *   };
 *
 *   ReactDOM.render(
 *     <App context={context}>
 *       <Layout>
 *         <LandingPage />
 *       </Layout>
 *     </App>,
 *     container,
 *   );
 */
class App extends React.PureComponent {
  
  static propTypes = {
    // Enables critical path CSS rendering
    // https://github.com/kriasoft/isomorphic-style-loader
    insertCss: PropTypes.func.isRequired,
    context: PropTypes.shape(ContextType).isRequired,
    children: PropTypes.element.isRequired,
  };

  // static childContextTypes = ContextType;

  // getChildContext() {
  //   return this.props.context;
  // }

  // NOTE: This methods are not needed if you update URL by setLocale action.

  componentDidMount() {
    const { context } = this.props;
    const store = context && context.store;
    if (store) {
      this.lastLocale = store.getState().intl.locale;
      this.unsubscribe = store.subscribe(() => {
        const state = store.getState();
        const { newLocale, locale } = state.intl;
        if (!newLocale && this.lastLocale !== locale) {
          this.lastLocale = locale;
          this.forceUpdate();
        }
      });
    }

    if (enableFirebase) {
      const messaging = getMessaging(app);
      onMessage(messaging, (payload) => {
        navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope').then(registration => {
          registration.showNotification(
            payload.notification.title,
            payload.notification
          )
        });
      });
    }

    const locale = context && context.locale;

    moment.updateLocale(locale + '-dup', { // Updating moment locale 
      parentLocale: isRTL(locale) ? locale : 'en',
      preparse: function (string) {
        return string;
      },
      postformat: function (string) {
        return string;
      }
    });
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  render() {
    // Here, we are at universe level, sure? ;-)
    //const { context, context: { store, locale }, insertCss } = this.props;
    const { context, insertCss } = this.props;
    const store = context && context.store;
    const state = store && store.getState();
    this.intl = (state && state.intl) || {};
    const { initialNow, locale, messages } = this.intl;
    const localeMessages = (messages && messages[locale]) || {};

    return (
      <ReduxProvider store={context.store}>
        <SocketContext.Provider value={socket}>
          <StyleContext.Provider value={{ insertCss }} >
            <ApplicationContext.Provider value={{ context }}>
              <IntlProvider
                initialNow={initialNow}
                locale={locale}
                messages={localeMessages}
                defaultLocale="en-US"
              >
                <ApolloProvider client={context.client} store={context.store}>
                  {React.Children.only(this.props.children)}
                </ApolloProvider>
              </IntlProvider>
            </ApplicationContext.Provider>
          </StyleContext.Provider>
        </SocketContext.Provider>
      </ReduxProvider>
    );
  }
}

export default App;
